// utils/saleIdempotencyKey.ts
//
// Pure, framework-independent piece of the POST /sales idempotency-key
// lifecycle used by SaleEntry.tsx. Kept separate from the component (which
// wires this to a useRef, localStorage draft persistence, and the submit
// handler) so the core "generate once, reuse until explicitly cleared" rule
// can be unit-tested without rendering the full form or mocking
// react-router/apiPost.

export type IdempotencyKeyRef = { current: string | null };

/**
 * Returns ref.current if already set, otherwise generates a new key via
 * generateKey(), stores it on the ref, and returns it. Callers own the
 * ref's lifetime: setting ref.current back to null (on confirmed success or
 * explicit discard) is what allows the next call to mint a new key -- this
 * function itself never rotates or clears a key on its own.
 *
 * Treats an empty-string ref value the same as null/absent. Production
 * code never intentionally sets ref.current to "" -- the only way this
 * branch is exercised is recovery from an invalid/corrupted local ref
 * state (e.g. a hand-edited or malformed persisted draft), in which case
 * minting a fresh UUID defensively is correct. This is purely local
 * client-side ref hygiene: it has no bearing on the backend's own
 * rejection of blank idempotency_key values, which is unchanged and
 * enforced independently of anything in this file.
 */
export function getOrCreateIdempotencyKey(
  ref: IdempotencyKeyRef,
  generateKey: () => string,
): string {
  if (!ref.current) {
    ref.current = generateKey();
  }
  return ref.current;
}

/** Default key generator: crypto.randomUUID(), already used elsewhere in
 * this codebase (see SaleDetail.tsx's offlinePayIdemRef) for the same
 * client-generated-idempotency-key pattern. */
export function randomUuidKey(): string {
  return crypto.randomUUID();
}

/**
 * Deterministic fingerprint of the exact Sale business payload (everything
 * that will be sent to POST /sales except idempotency_key itself). Callers
 * must pass the SAME object that (with idempotency_key added) becomes the
 * actual request body -- never a separately reconstructed copy -- so the
 * fingerprint can never drift from what is truly sent. See SaleEntry.tsx
 * for the determinism analysis (fixed literal key order, no dynamic key
 * insertion, no unstable value normalization) that makes plain
 * JSON.stringify safe here.
 */
export function fingerprintSalePayload(payloadWithoutIdempotencyKey: unknown): string {
  return JSON.stringify(payloadWithoutIdempotencyKey);
}

/**
 * True iff `fingerprint` is a pre-2B ("old-shape") POST /sales payload
 * fingerprint: a parseable JSON object that carried a bare `fee` but NO
 * `fee_mode` discriminator (exactly what a paid sale posted before Phase 2B).
 * A post-2B paid sale always carries `fee_mode`; a no-payment sale carries
 * neither field in either era, so it is correctly NOT flagged (an unchanged
 * no-payment retry stays a normal unchanged-retry). Used to pick distinct,
 * non-misleading messaging on a cross-deploy mismatch -- see
 * SALE_LEGACY_SHAPE_BLOCKED_MESSAGE. Unparseable strings return false.
 */
export function isLegacySaleFingerprint(fingerprint: string | null | undefined): boolean {
  if (!fingerprint) return false;
  try {
    const obj = JSON.parse(fingerprint);
    return (
      !!obj && typeof obj === "object" && "fee" in obj && !("fee_mode" in obj)
    );
  } catch {
    return false;
  }
}

export type SaleSubmissionIdentityDecision =
  | "first-attempt"
  | "unchanged-retry"
  | "mismatch";

/**
 * Pure branching logic for whether a submit attempt is the first real
 * attempt for this logical Sale draft, a retry of the exact same payload,
 * or a retry of an EDITED payload (which must be blocked -- see
 * SaleEntry.tsx's submitSale).
 */
export function decideSaleSubmissionIdentity(
  storedFingerprint: string | null,
  currentFingerprint: string,
): SaleSubmissionIdentityDecision {
  if (storedFingerprint === null) return "first-attempt";
  if (storedFingerprint === currentFingerprint) return "unchanged-retry";
  return "mismatch";
}

/**
 * Messaging for a blocked "mismatch" submit. An edited retry of a CURRENT-shape
 * draft may safely be retried unchanged; a pre-2B (old-shape) draft can never
 * match a current-shape retry, so it must NOT be told an unchanged retry
 * resolves it -- it is directed to verify history instead. The bound
 * key/fingerprint are left untouched in both cases (never rebound/cleared).
 */
export const SALE_EDITED_RETRY_BLOCKED_MESSAGE =
  "This sale may already have been submitted. Retry it without changes, or " +
  "cancel and start a new sale.";

export const SALE_LEGACY_SHAPE_BLOCKED_MESSAGE =
  "This sale draft was started in an earlier version of the app, so it can't " +
  "be safely retried from here. Check your sales history to confirm whether it " +
  "was already recorded before starting a new sale.";
